import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	AfterViewInit,
	ViewChildren,
	QueryList,
} from '@angular/core';
import { MfaLoginDataWithToken } from '../../../models/User/mfa/mfa-tokens/mfa-login-data';
import { ScreenWidthListenerService } from '../../../services/screen-width-detector/screen-width-listener.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ActivatedRoute } from '@angular/router';
import { MfaLoginService } from '../../../services/authentication/mfa-login.service';
import { MfaVerifyViewModel } from './models/mfa-verify-view-model';
import { MfaType } from '../../../models/enums/mfa-type';
import { MfaApiService } from '../../../server-api/mfa-api.service';
import { SendCodeMfaEmailInput } from '../../../models/User/mfa/user-mfa-configuration/send-code-mfa-email-input';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilitiesService } from '../../../services/utils/utilities.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { SendCodeMfaEmailResult } from '../../../models/User/mfa/user-mfa-configuration/send-code-mfa-email-result';
import { RequestMfaLoginInput } from '../../../models/User/mfa/user-mfa-configuration/request-mfa-login-input';
import { UserMfaAuthenticator } from '../../../models/User/mfa/user-mfa-configuration/user-mfa-authenticator';
import { CheckCanRequestNewMfaEmailCodeInput } from '../../../models/User/mfa/user-mfa-configuration/check-can-request-new-mfa-email-code-input';
import { finalize, Subscription } from 'rxjs';
import { SessionStatusBroadcastService } from '../../../services/authorization/session-status-broadcast.service';
import { BroadcastMessage } from '../../../models/broadcast/broadcast-message';
import { BroadcastMessageType } from '../../../models/broadcast/broadcast-message-type';
import { LogService } from '../../../services/log/log.service';
import { GetTokenResult } from '../../../models/User/auth/get-token-result';
import { ServerErrors } from '../../../models/enums/server-errors';
import { LoginService } from '../../../services/authentication/login.service';

@Component({
	selector: 'eif-mfa-verify',
	templateUrl: './mfa-verify.component.html',
	styleUrls: ['./mfa-verify.component.scss'],
	standalone: false,
})
export class MfaVerifyComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('mfaVerifyCode') private mfaVerifyCode: ElementRef;
	@ViewChild('input0', { read: ElementRef }) input0!: ElementRef;
	@ViewChild('input1', { read: ElementRef }) input1!: ElementRef;
	@ViewChild('input2', { read: ElementRef }) input2!: ElementRef;
	@ViewChild('input3', { read: ElementRef }) input3!: ElementRef;
	@ViewChild('input4', { read: ElementRef }) input4!: ElementRef;
	@ViewChild('input5', { read: ElementRef }) input5!: ElementRef;

	@ViewChildren('input0, input1, input2, input3, input4, input5', { read: ElementRef })
	mfaInputsQueryList!: QueryList<ElementRef>;

	private static REQUIRED_CODE_LENGTH = 6;
	private static FIRST_CODE_INPUT_POSITION = 1;
	private static LAST_CODE_INPUT_POSITION = 6;

	public isPerformingRequest: boolean;
	private selectedAuthenticator: UserMfaAuthenticator;
	protected mfaData: MfaLoginDataWithToken;
	protected viewModel: MfaVerifyViewModel;
	protected showOtpVerificationLayout: Promise<boolean> = Promise.resolve(false);
	protected isFatalError: Promise<boolean> = Promise.resolve(false);
	protected invalidOrExpiredOtpVerificationCode: boolean = false;
	private codeExpirationCheckerInterval: any = null;
	private _canRequestNewCode: boolean = false;
	private requestMfaVerifyTimeoutId: any = null;

	private countdownInterval: any = null;
	private _remainingSeconds: number = 0;
	private static readonly CODE_REQUEST_COOLDOWN_SECONDS = 300;
	private sessionKilledSubscription: Subscription;

	private mfaInputElements!: ElementRef[];
	private mfaValues: string[] = ['', '', '', '', '', ''];
	private readonly mfaLength: number = 6;
	public isVerified: boolean = false;
	public isSubmittingMfa: boolean = false;

	public isReady = false;
	public mfaTitle = '';

	constructor(
		protected screenWidthListener: ScreenWidthListenerService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private mfaLoginService: MfaLoginService,
		private loginService: LoginService,
		private mfaApiService: MfaApiService,
		private utilitiesService: UtilitiesService,
		private toasterService: ToasterService,
		private sessionStatusBroadcastService: SessionStatusBroadcastService,
		private logger: LogService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.mfaLoginService.getMfaLoginDataFromRoute(this.route).then((mfaData: MfaLoginDataWithToken) => {
			this.mfaData = mfaData;
			this.selectedAuthenticator = this.mfaData.SelectedMfaAuthenticator!;
			if (!!this.mfaData?.MfaToken && !!this.mfaData?.MfaLoginData && !!this.selectedAuthenticator) {
				if (this.selectedAuthenticator.MfaTypeName === MfaType.OTP) {
					this.subscribeSessionKill();
					this.loadMfaVerificationOtp();
				} else if (this.selectedAuthenticator.MfaTypeName === MfaType.EMAIL) {
					this.subscribeSessionKill();
					this.loadMfaVerificationEmail();
				} else {
					this.mfaLoginService.cancelMfaLogin();
				}
			} else {
				this.mfaLoginService.cancelMfaLogin();
			}
		});
		setTimeout(() => {
			this.mfaTitle = this.translateService.translate('mfa-verify.title', 'client');
			this.isReady = true;
			this.cdr.markForCheck();
		}, 0);
	}

	ngAfterViewInit(): void {
		this.mfaInputsQueryList.changes.subscribe(() => {
			this.initializeMfaInputElements();
		});

		this.initializeMfaInputElements();
	}

	private initializeMfaInputElements(): void {
		if (this.mfaInputsQueryList.length === 6) {
			this.mfaInputElements = this.mfaInputsQueryList.toArray();
			return;
		}

		if (this.input0 && this.input1 && this.input2 && this.input3 && this.input4 && this.input5) {
			this.mfaInputElements = [this.input0, this.input1, this.input2, this.input3, this.input4, this.input5];
			return;
		}
	}

	ngOnDestroy(): void {
		this.clearCodeExpirationChecker();
		this.clearCountdownTimer();
		this.unsubscribeSessionKill();
	}

	private subscribeSessionKill(): void {
		this.sessionKilledSubscription = this.sessionStatusBroadcastService
			.messagesOfType(BroadcastMessageType.SessionKilled)
			.subscribe((msg: BroadcastMessage) => {
				if (msg.type == BroadcastMessageType.SessionKilled) {
					this.unsubscribeSessionKill();
					this.mfaLoginService.cancelMfaLogin();
				}
			});
	}

	private unsubscribeSessionKill(): void {
		if (!!this.sessionKilledSubscription) {
			this.sessionKilledSubscription.unsubscribe();
		}
	}

	protected get canRequestNewCode(): boolean {
		return this._canRequestNewCode;
	}

	protected get remainingSeconds(): number {
		return this._remainingSeconds;
	}

	protected get remainingTimeFormatted(): string {
		const minutes = Math.floor(this._remainingSeconds / 60);
		const seconds = this._remainingSeconds % 60;
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	protected get requestNewCodeButtonText(): string {
		if (this.canRequestNewCode) {
			return this.translateService.translate('mfa-verify.requestNewCode', 'client');
		} else if (this._remainingSeconds > 0) {
			return `${this.remainingTimeFormatted} ${this.translateService.translate('mfa-verify.waitBeforeRequestNewCode', 'client')}`;
		} else {
			return this.translateService.translate('mfa-verify.waitBeforeRequestNewCode', 'client');
		}
	}

	protected get requestNewCodeButtonTooltip(): string {
		if (this.canRequestNewCode) {
			return this.translateService.translate('mfa-verify.requestNewCode', 'client');
		} else {
			return this.translateService.translate('mfa-verify.waitBeforeRequestNewCode', 'client');
		}
	}

	protected requestNewCode(): void {
		if (!this.canRequestNewCode) {
			return;
		}

		if (this.viewModel.MfaTypeName === MfaType.EMAIL) {
			this.loadMfaVerificationEmail();
		}
	}

	protected chooseAnotherAuthenticator(): void {
		if (!this.hasOptionChooseOtherAuthenticators()) {
			return;
		}

		this.mfaLoginService.goToNextMfaPage(this.mfaData.MfaLoginData);
	}

	protected handleCodePaste(event): void {
		event.preventDefault();
		event.stopPropagation();

		const pasted: string = event.clipboardData?.getData('text/plain') ?? event.target.value;
		const pastedChars: string[] = pasted?.split('');
		if (!pastedChars || pastedChars.length === 0) {
			this.clearMfaInputs();
			return;
		}

		for (let index = 0; index < MfaVerifyComponent.REQUIRED_CODE_LENGTH; index++) {
			const char = pastedChars[index];
			if (char) {
				const position = index + 1;
				if (position >= MfaVerifyComponent.FIRST_CODE_INPUT_POSITION && position <= MfaVerifyComponent.LAST_CODE_INPUT_POSITION) {
					this.viewModel[`CodeChar${position}`] = char;
				}
			}
		}

		this.tryToSubmit();
	}

	protected handleCodeChange(event: KeyboardEvent, position: number): void {
		if ((event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === 'V')) {
			return;
		}

		const value = this.getCodeInput(position)?.value ?? '';

		if (value?.length === MfaVerifyComponent.REQUIRED_CODE_LENGTH) {
			return;
		}

		if (value?.length >= 1) {
			const nextInput = this.getNextCodeInput(position);
			if (!!nextInput) {
				nextInput.focus();
			}
		} else {
			const prevInput = this.getPreviousCodeInput(position);
			if (!!prevInput) {
				prevInput.focus();
			}
		}

		if (event.type === 'keyup' && position === MfaVerifyComponent.LAST_CODE_INPUT_POSITION) {
			this.tryToSubmit();
		}
	}

	protected handleCodeFocus(event: FocusEvent, position: number): void {
		if (position > MfaVerifyComponent.FIRST_CODE_INPUT_POSITION) {
			const firstCodeInput = this.getCodeInput(MfaVerifyComponent.FIRST_CODE_INPUT_POSITION);
			if (!!firstCodeInput && !firstCodeInput.value) {
				event.preventDefault();
				event.stopPropagation();
				firstCodeInput.focus();
			}
		}
	}

	protected handleKeyDown(event: KeyboardEvent, position: number): void {
		const value = this.getCodeInput(position)?.value ?? '';
		if (event?.key === 'Backspace') {
			if (!value) {
				const prevInput = this.getPreviousCodeInput(position);
				if (!!prevInput) {
					prevInput.focus();
				}
			}
		} else {
			if (value?.length >= 1 && event.key.length === 1) {
				const newValue = parseInt(event.key);
				if (!!newValue || newValue === 0) {
					const nextInput = this.getNextCodeInput(position);
					if (!!nextInput) {
						nextInput.focus();
					}
				}
			}
		}
	}

	protected cancelLogin(): void {
		this.clearCodeExpirationChecker();
		this.mfaLoginService.cancelMfaLogin();
	}

	private loadMfaVerificationOtp(): void {
		const userMfaAuthenticator = this.mfaData.MfaLoginData.UserMfaAuthenticators.find((userMfa) => userMfa.MfaTypeName === MfaType.OTP);
		if (!userMfaAuthenticator || !userMfaAuthenticator.MfaDeviceName) {
			this.cancelLogin();
			return;
		}

		this.viewModel = new MfaVerifyViewModel();
		this.viewModel.MfaTypeName = MfaType.OTP;
		this.viewModel.HasOptionRequestNewCode = false;
		this.viewModel.HasOptionChooseOtherAuthenticators = this.hasOptionChooseOtherAuthenticators();

		this.viewModel.MfaInfoIconUrl = 'assets/images/icon_dark_mfa_authenticator_app.svg';
		this.viewModel.MfaTypeTitle = this.translateService.translate('mfa-verify.mfaAuthenticatorTitleOtp', 'client');
		this.viewModel.MfaTypeDescription = this.translateService
			.translate('mfa-verify.mfaAuthenticatorDescriptionOtp', 'client')
			.replace('[DEVICE_NAME]', userMfaAuthenticator.MfaDeviceName);

		this.showOtpVerificationLayout = Promise.resolve(true);
	}

	private loadMfaVerificationEmail(): void {
		this.viewModel = new MfaVerifyViewModel();
		this.viewModel.MfaTypeName = MfaType.EMAIL;
		this.viewModel.HasOptionRequestNewCode = true;
		this.viewModel.HasOptionChooseOtherAuthenticators = this.hasOptionChooseOtherAuthenticators();

		this.sendCodeMfaEmail().then((result: SendCodeMfaEmailResult) => {
			this.viewModel.MfaInfoIconUrl = 'assets/images/icon_dark_mfa_authenticator_email.svg';
			this.viewModel.MfaTypeTitle = this.translateService.translate('mfa-verify.mfaAuthenticatorTitleEmail', 'client');
			this.viewModel.MfaTypeDescription = this.translateService
				.translate('mfa-verify.mfaAuthenticatorDescriptionEmail', 'client')
				.replace('[EMAIL]', result.CodeRecipientEmailObfuscated);
			this.viewModel.CodeAreaFootnoteText = this.translateService.translate('mfa-verify.mfaVerificationCodeFootnoteTextEmail', 'client');

			this.triggerCodeExpirationChecker();

			this.showOtpVerificationLayout = Promise.resolve(true);
		});
	}

	private sendCodeMfaEmail(): Promise<SendCodeMfaEmailResult> {
		return new Promise<SendCodeMfaEmailResult>((resolve, _reject) => {
			this.clearOldInlineErrorIndicators();

			const inputDto: SendCodeMfaEmailInput = {
				MfaToken: this.mfaData.MfaToken,
			};

			this.isPerformingRequest = true;

			this.mfaApiService.sendCodeMfaEmail(inputDto).subscribe({
				next: (operationResult: OperationResult<SendCodeMfaEmailResult>) => {
					if (operationResult.IsValid && !!operationResult.Data) {
						this._canRequestNewCode = false;
						this.startCountdownTimer();
						resolve(operationResult.Data);
					} else {
						this.handleMfaLoginOperationErrors(operationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.processHttpErrorResponse(httpErrorResponse);
				},
				complete: () => {
					this.isPerformingRequest = false;
				},
			});
		});
	}

	private hasOptionChooseOtherAuthenticators(): boolean {
		if (!!this.mfaData?.MfaLoginData?.UserMfaAuthenticators) {
			const countActiveAuthenticators = this.mfaData.MfaLoginData.UserMfaAuthenticators.filter((userMfa) => userMfa.IsActive).length;

			if (countActiveAuthenticators > 1 || countActiveAuthenticators === 0) {
				return true;
			}
		}

		return false;
	}

	private checkCanRequestNewCode(): Promise<void> {
		return new Promise<void>((resolve, _reject) => {
			const inputDto: CheckCanRequestNewMfaEmailCodeInput = {
				MfaToken: this.mfaData.MfaToken,
			};

			this.mfaApiService.checkCanRequestNewMfaEmailCode(inputDto).subscribe({
				next: (operationResult: OperationResult<boolean>) => {
					if (operationResult.IsValid) {
						this._canRequestNewCode = operationResult.Data;

						if (this._canRequestNewCode) {
							this.clearCountdownTimer();
						}

						resolve();
					} else {
						this.handleMfaLoginOperationErrors(operationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.processHttpErrorResponse(httpErrorResponse);
				},
			});
		});
	}

	private clearCodeExpirationChecker(): void {
		if (!!this.codeExpirationCheckerInterval) {
			clearInterval(this.codeExpirationCheckerInterval);
			this.codeExpirationCheckerInterval = null;
		}
	}

	private startCountdownTimer(): void {
		this.clearCountdownTimer();
		this._remainingSeconds = MfaVerifyComponent.CODE_REQUEST_COOLDOWN_SECONDS;

		this.countdownInterval = setInterval(() => {
			this._remainingSeconds--;

			if (this._remainingSeconds <= 0) {
				this.clearCountdownTimer();

				this.checkCanRequestNewCode();
			}
		}, 1000);
	}

	private clearCountdownTimer(): void {
		if (!!this.countdownInterval) {
			clearInterval(this.countdownInterval);
			this.countdownInterval = null;
		}
		this._remainingSeconds = 0;
	}

	private triggerCodeExpirationChecker(): void {
		this.clearCodeExpirationChecker();
		this.startCountdownTimer();

		this.codeExpirationCheckerInterval = setInterval(() => {
			this.checkCanRequestNewCode().then(() => {
				if (this.canRequestNewCode) {
					this.clearCodeExpirationChecker();
					this.clearCountdownTimer();
				}
			});
		}, 60000);
	}

	private getNextCodeInput(position: number): HTMLInputElement | null {
		return this.getCodeInput(position + 1);
	}

	private getPreviousCodeInput(position: number): HTMLInputElement | null {
		return this.getCodeInput(position - 1);
	}

	private getCodeInput(position: number): HTMLInputElement | null {
		if (position >= MfaVerifyComponent.FIRST_CODE_INPUT_POSITION && position <= MfaVerifyComponent.LAST_CODE_INPUT_POSITION) {
			return this.mfaVerifyCode?.nativeElement.querySelector(`#mfaVerifyCodeChar${position}`) as HTMLInputElement;
		}
		return null;
	}

	public tryToSubmit(): void {
		const code = this.getMfaCode();
		const hasCompleteCode = code?.length === MfaVerifyComponent.REQUIRED_CODE_LENGTH;
		if (hasCompleteCode) {
			if (!!this.requestMfaVerifyTimeoutId) {
				return;
			}
			this.requestMfaLogin();
		}
	}

	private requestMfaLogin(): void {
		this.clearOldInlineErrorIndicators();

		const inputDto: RequestMfaLoginInput = {
			MfaToken: this.mfaData.MfaToken,
			MfaTypeName: this.viewModel.MfaTypeName,
			VerificationCode: this.getMfaCode(),
		};

		this.isPerformingRequest = true;
		this.isSubmittingMfa = true;

		this.mfaApiService
			.getJwtByMfa(inputDto)
			.pipe(
				finalize(() => {
					this.isPerformingRequest = false;
					this.isSubmittingMfa = false;

					if (!!this.requestMfaVerifyTimeoutId) {
						this.requestMfaVerifyTimeoutId = null;
					}
				})
			)
			.subscribe({
				next: (operationResult: OperationResult<GetTokenResult>) => {
					if (operationResult.IsValid && !!operationResult.Data) {
						this.mfaLoginService.handleMfaLoginSuccess(this.mfaData.MfaLoginData, operationResult.Data);
					} else {
						this.handleMfaLoginOperationErrors(operationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.processHttpErrorResponse(httpErrorResponse);
				},
			});
	}

	private handleMfaLoginOperationErrors<T>(operationResult: OperationResult<T>): void {
		const unauthorizedError = operationResult.Errors?.find((error) => error.Code === ServerErrors.Unauthorized);
		const invalidOrExpiredOtpVerificationCodeError = operationResult.Errors?.find(
			(error) => error.Code === ServerErrors.InvalidOrExpiredOtpVerificationCode
		);
		const authProfileSelectionRequiredError = operationResult.Errors?.find(
			(error) => error.Code === ServerErrors.AuthProfileSelectionRequired
		);

		if (!!unauthorizedError) {
			this.cancelLogin();
		} else if (!!invalidOrExpiredOtpVerificationCodeError) {
			this.handleInvalidVerificationCode();
		} else if (!!authProfileSelectionRequiredError) {
			this.loginService.handleAuthPendingProfileSelection(
				this.mfaData.MfaLoginData.Username!,
				authProfileSelectionRequiredError,
				this.mfaData.MfaLoginData.ReturnUrl
			);
		} else {
			this.processInvalidResult(operationResult);
		}
	}

	private handleInvalidVerificationCode(): void {
		this.invalidOrExpiredOtpVerificationCode = true;
		this.isVerified = false;
		this.isSubmittingMfa = false;
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>): void {
		const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
		if (errors.length > 0) {
			this.isFatalError = Promise.resolve(true);
			this.showOtpVerificationLayout = Promise.resolve(false);
		}
		this.isSubmittingMfa = false;
	}

	private processHttpErrorResponse(httpErrorResponse: HttpErrorResponse): void {
		this.logger.error('[MfaVerify]:: HttpErrorResponse>', httpErrorResponse);
		this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
		this.isFatalError = Promise.resolve(true);
		this.showOtpVerificationLayout = Promise.resolve(false);
		this.isSubmittingMfa = false;
	}

	private clearOldInlineErrorIndicators(): void {
		this.invalidOrExpiredOtpVerificationCode = false;
	}

	onInputChange(_inputName: string, _value: string): void {}

	onMfaInputChange(value: string, index: number): void {
		this.clearOldInlineErrorIndicators();

		const digit = value.replace(/[^0-9]/g, '');

		if (!this.mfaInputElements || this.mfaInputElements.some((el) => !el)) {
			this.initializeMfaInputElements();
		}

		this.mfaValues[index] = digit;

		const inputElement = this.mfaInputElements[index]?.nativeElement?.querySelector('input');
		if (inputElement && inputElement.value !== digit) {
			inputElement.value = digit;
		}

		if (digit && index < this.mfaInputElements.length - 1) {
			this.focusInput(index + 1);
		}

		const allFieldsFilled = this.mfaValues.every((value) => value !== '');

		if (allFieldsFilled && this.mfaValues.join('').length === this.mfaLength) {
			this.isVerified = true;
			this.tryToSubmit();
		} else {
			this.isVerified = false;
		}
	}

	onMfaInputFocus(_index: number): void {}

	onMfaKeydown(event: KeyboardEvent): void {
		const target = event.target as HTMLInputElement;
		const currentIndex = this.getCurrentInputIndex(target);

		if (currentIndex !== -1) {
			if (
				event.key === 'Backspace' ||
				event.key === 'Delete' ||
				event.key === 'ArrowLeft' ||
				event.key === 'ArrowRight' ||
				event.key === 'Tab' ||
				event.key === 'Enter' ||
				event.ctrlKey ||
				event.metaKey
			) {
				if (event.key === 'Backspace' && target.value === '' && currentIndex > 0) {
					event.preventDefault();
					this.focusInput(currentIndex - 1);
				} else if (event.key === 'ArrowLeft' && currentIndex > 0) {
					event.preventDefault();
					this.focusInput(currentIndex - 1);
				} else if (event.key === 'ArrowRight' && currentIndex < this.mfaInputElements.length - 1) {
					event.preventDefault();
					this.focusInput(currentIndex + 1);
				}
				return;
			}

			if (!/^[0-9]$/.test(event.key)) {
				event.preventDefault();
				return;
			}

			if (target.value && event.key.length === 1) {
				target.value = '';

				this.mfaValues[currentIndex] = '';
			}
		}
	}

	onMfaPaste(event: ClipboardEvent): void {
		event.preventDefault();

		this.clearOldInlineErrorIndicators();

		const pastedData = event.clipboardData?.getData('text') || '';
		const digits = pastedData.replace(/[^0-9]/g, '');

		for (let i = 0; i < this.mfaLength; i++) {
			const inputElement = this.mfaInputElements[i]?.nativeElement?.querySelector('input');
			if (inputElement) {
				inputElement.value = '';
			}
			this.mfaValues[i] = '';
		}

		for (let i = 0; i < Math.min(digits.length, this.mfaLength); i++) {
			this.mfaValues[i] = digits[i];

			const inputElement = this.mfaInputElements[i]?.nativeElement?.querySelector('input');
			if (inputElement) {
				inputElement.value = digits[i];
			}
		}

		const nextEmptyIndex = this.mfaValues.findIndex((value) => value === '');
		const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(digits.length, this.mfaLength) - 1;

		if (focusIndex >= 0) {
			this.focusInput(focusIndex);
		}

		const allFieldsFilled = this.mfaValues.every((value) => value !== '');
		const mfaCode = this.mfaValues.join('');

		if (allFieldsFilled && mfaCode.length === this.mfaLength) {
			this.isVerified = true;

			setTimeout(() => {
				this.tryToSubmit();
			}, 100);
		} else {
			this.isVerified = false;
		}
	}

	private getCurrentInputIndex(target: HTMLInputElement): number {
		for (let i = 0; i < this.mfaInputElements.length; i++) {
			const inputElement = this.mfaInputElements[i]?.nativeElement?.querySelector('input');
			if (inputElement === target) {
				return i;
			}
		}
		return -1;
	}

	private focusInput(index: number): void {
		if (this.mfaInputElements && this.mfaInputElements[index]) {
			const inputElement = this.mfaInputElements[index].nativeElement?.querySelector('input');
			if (inputElement) {
				inputElement.focus();
			}
		}
	}

	clearMfaInputs(): void {
		this.mfaValues = ['', '', '', '', '', ''];
		this.isVerified = false;

		for (let i = 0; i < this.mfaLength; i++) {
			const inputElement = this.mfaInputElements[i]?.nativeElement?.querySelector('input');
			if (inputElement) {
				inputElement.value = '';
			}
		}
		this.focusInput(0);
	}

	getMfaCode(): string {
		return this.mfaValues.join('');
	}
}
